// @fastn/core — the product-analytics contract.
//
// One place that defines what may be sent to PostHog, from anywhere: the
// dashboard, the embed, and every service. Two things live here and nothing
// else does — the typed event catalogue, and the property envelope every event
// carries.
//
// Why a discriminated union rather than `capture(name, props)`:
//   A typo in an event name is otherwise a silent second event that halves a
//   funnel, and nobody notices for a quarter. Here it is a compile error. The
//   same goes for a renamed property: the funnel that depended on it fails to
//   build rather than quietly returning zero.
//
// Naming rules, all enforced by tests/unit/analytics-catalogue.test.ts:
//   - object_verb, snake_case, verb in the past tense
//   - objects come from the domain (connector, connection, widget, workflow,
//     tenant, agent) — never an analytics-only noun
//   - never prefixed by surface. `dashboard_connector_connected` and
//     `embed_connector_connected` split one funnel into two; `surface` is a
//     property. The hub_* events are the deliberate exception: a different
//     population doing a different thing.
//   - never `$`-prefixed — reserved by PostHog
//   - durations end _ms, counts end _count, booleans start is_
import { lookupError } from "./errors/registry.js";
/**
 * Codes whose bucket is not implied by their HTTP status. QUOTA_EXCEEDED and
 * PAYMENT_REQUIRED both share 429/402 with ordinary throttling, and conflating
 * them would hide the entire monetization signal inside `rate_limit`.
 */
const CODE_OVERRIDES = {
    QUOTA_EXCEEDED: "quota",
    PAYMENT_REQUIRED: "quota",
    AUTH_INVALID: "auth",
    AUTH_EXPIRED: "auth",
};
const STATUS_BUCKETS = [
    [401, "auth"],
    [402, "quota"],
    [403, "permission"],
    [404, "not_found"],
    [405, "validation"],
    [409, "conflict"],
    [413, "validation"],
    [422, "validation"],
    [429, "rate_limit"],
    [502, "upstream"],
    [503, "upstream"],
    [504, "timeout"],
];
/**
 * Map a structured error code onto the analytics taxonomy.
 *
 * ONE implementation, shared by the client and every service, so a
 * client-reported `error_shown` and a server-reported `connect_failed` can be
 * joined on failure_category without translating between two vocabularies.
 *
 * Total by construction: an unrecognised code resolves through the registry's
 * own fallback entry (status 500) and lands on `internal` rather than throwing,
 * so a service that invents a code tomorrow still charts.
 */
export function failureCategory(code) {
    if (!code)
        return "unknown";
    const override = CODE_OVERRIDES[code];
    if (override)
        return override;
    return failureCategoryForStatus(lookupError(code).status);
}
/**
 * The same taxonomy from an HTTP status alone, for a failure that came back
 * with no structured code (an HTML error page from a proxy, a bare 502, a
 * response body nobody parsed). The code-based form above defers to this, so
 * the two cannot disagree about a status.
 */
export function failureCategoryForStatus(status) {
    if (!status)
        return "unknown";
    for (const [code_, bucket] of STATUS_BUCKETS) {
        if (status === code_)
            return bucket;
    }
    if (status >= 400 && status < 500)
        return "validation";
    if (status >= 500)
        return "internal";
    return "unknown";
}
// ── Credential redaction ─────────────────────────────────────────────────────
/**
 * Credential shapes that must never reach analytics, in any property.
 *
 * Lives here, beside the event catalogue, for the same reason the catalogue
 * does: the browser and the services both send events, so a scrub that exists
 * on only one side is a scrub with a hole in it. The browser was the side that
 * leaked (KAT-6483) and the services are the side with no free-text properties
 * today — but "today" is doing a lot of work in that sentence, and the next
 * event with a `message` field should not have to remember this.
 *
 * Matched by SHAPE rather than by property name, deliberately. The leak was in
 * `$el_text`, an autocapture property nobody names and nobody controls, on
 * elements nobody had marked. A name-based filter would have missed it.
 *
 * Slack tokens are covered because a partner's workspace token is exactly as
 * damaging as one of ours, and it is the kind nobody thinks to look for.
 */
const CREDENTIAL_RE = new RegExp([
    "fsk_(?:live|test)_[A-Za-z0-9_-]{8,}", // fastn API keys
    "xox[abprs]-[A-Za-z0-9-]{10,}", // Slack bot/user/app tokens
    "xapp-\\d-[A-Za-z0-9-]{10,}", // Slack app-level tokens
    "https?://hooks\\.slack\\.com/services/[A-Za-z0-9/]+", // Slack webhooks (scheme included, or the URL is only half-redacted)
    "Bearer\\s+[A-Za-z0-9._~+/-]{12,}={0,2}", // bearer tokens
    "ey[A-Za-z0-9_-]{8,}\\.[A-Za-z0-9_-]{8,}\\.[A-Za-z0-9_-]+", // JWTs
    "gh[pousr]_[A-Za-z0-9]{16,}", // GitHub tokens
    "github_pat_[A-Za-z0-9_]{20,}", // GitHub fine-grained PATs
    "sk-[A-Za-z0-9_-]{16,}", // OpenAI-style secret keys
    "AKIA[0-9A-Z]{16}", // AWS access key ids
    "AIza[0-9A-Za-z_-]{20,}", // Google API keys
].join("|"), "gi");
/** How deep to walk a property value. Autocapture's `$elements` is an array of
 *  objects one level down; beyond that is payload neither side sends. Also the
 *  thing that stops a cyclic object hanging the walk. */
const REDACT_MAX_DEPTH = 4;
/**
 * Replace anything credential-shaped with a marker, in place, at any depth.
 *
 * Cheap on the common path: strings that match nothing are returned by
 * identity, and non-strings are walked only when they are plain objects or
 * arrays. That matters because this runs on EVERY event, and browser
 * autocapture alone was 46,551 of them in five days.
 */
export function redactSecrets(value, depth = 0) {
    if (typeof value === "string") {
        // `test` then `replace` rather than replace alone: the overwhelming
        // majority of strings match nothing, and this skips building a new one.
        // lastIndex is reset around BOTH calls because the regex is global —
        // without that, the second identical event silently ships its key.
        CREDENTIAL_RE.lastIndex = 0;
        if (!CREDENTIAL_RE.test(value))
            return value;
        CREDENTIAL_RE.lastIndex = 0;
        return value.replace(CREDENTIAL_RE, "[redacted]");
    }
    if (depth >= REDACT_MAX_DEPTH || value === null || typeof value !== "object")
        return value;
    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++)
            value[i] = redactSecrets(value[i], depth + 1);
        return value;
    }
    const obj = value;
    for (const k of Object.keys(obj))
        obj[k] = redactSecrets(obj[k], depth + 1);
    return value;
}
/** Longest error text sent. Enough to read the sentence, not enough to carry a payload. */
export const ERROR_TEXT_MAX = 300;
const ERROR_TEXT_SCRUBS = [
    [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
    [/https?:\/\/[^\s"'<>]+/g, "[url]"],
    // A bare domain is usually the person's own product, typed during setup.
    [/\b(?:[a-z0-9-]+\.)+[a-z]{2,}\b/gi, "[domain]"],
    [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "[id]"],
    [/\bucl:[^\s"',]+/g, "[id]"],
    [/\b[a-z]{2,12}_[A-Za-z0-9-]{6,}\b/g, "[id]"],
    [/\b\d{5,}\b/g, "[n]"],
    // A quoted value is usually something the person typed or a record field.
    [/"[^"\n]{1,200}"|'[^'\n]{1,200}'|`[^`\n]{1,200}`/g, "\"…\""],
];
/**
 * The text of an error, made safe to send: credentials, emails, URLs, ids and
 * quoted values replaced, whitespace collapsed, capped at ERROR_TEXT_MAX.
 *
 * Error text is the one free-text property the catalogue carries, and it is
 * there because "which error did the person see" cannot be answered from a
 * code. What a message can echo back (a typed value, a record id, a customer's
 * email) is exactly what this removes; the sentence around it stays readable.
 */
export function scrubErrorText(raw) {
    if (typeof raw !== "string")
        return null;
    let s = redactSecrets(raw);
    for (const [re, mark] of ERROR_TEXT_SCRUBS)
        s = s.replace(re, mark);
    s = s.replace(/\s+/g, " ").trim();
    if (!s)
        return null;
    return s.length > ERROR_TEXT_MAX ? `${s.slice(0, ERROR_TEXT_MAX - 1)}…` : s;
}
/** Email domains whose users are fastn staff rather than customers. */
const INTERNAL_EMAIL_DOMAINS = new Set(["fastn.ai"]);
/**
 * The single definition of "this is us, not a customer".
 *
 * Belt and braces: PostHog also carries a project-level filter on this
 * property AND on the email domain, because a new surface that forgets to set
 * the envelope would otherwise silently readmit staff traffic.
 */
export function isInternalEmail(email) {
    if (!email)
        return false;
    const at = email.lastIndexOf("@");
    if (at < 0)
        return false;
    return INTERNAL_EMAIL_DOMAINS.has(email.slice(at + 1).toLowerCase());
}
/**
 * Runtime list of every event name, for the tests that check each one has a
 * call site and that no raw string escapes the union. Kept in sync with the
 * type by analytics-catalogue.test.ts, which fails if the two diverge.
 */
export const ANALYTICS_EVENTS = [
    "onboarding_questions_answered",
    "onboarding_started",
    "systems_detected",
    "onboarding_step_completed",
    "widget_created",
    "embed_snippet_copied",
    "onboarding_completed",
    "onboarding_abandoned",
    "onboarding_screen_left",
    "onboarding_step_failed",
    "onboarding_screen_viewed",
    "onboarding_action",
    "onboarding_milestone",
    "setup_step_done",
    "home_step_clicked",
    "home_prompt_sent",
    "connect_started",
    "connect_redirected",
    "connect_succeeded",
    "connect_failed",
    "connector_requested",
    "connection_auth_expired",
    "connection_disconnected",
    "connection_reauthorised",
    "connector_action_failed",
    "hub_opened",
    "hub_connect_succeeded",
    "hub_connect_failed",
    "hub_search_zero_results",
    "hub_session_expired",
    "workflow_created",
    "workflow_tested",
    "workflow_published",
    "workflow_execution_succeeded",
    "workflow_execution_failed",
    "trigger_bound",
    "connector_changed",
    "widget_changed",
    "workflow_changed",
    "schedule_changed",
    "webhook_changed",
    "unified_api_changed",
    "config_changed",
    "tenant_created",
    "environment_created",
    "github_connected",
    "event_replayed",
    "link_created",
    "alert_rule_created",
    "execution_detail_opened",
    "agent_thread_started",
    "agent_proposal_shown",
    "agent_proposal_resolved",
    "agent_feedback_submitted",
    "agent_run_blocked",
    "agent_session_started",
    "agent_tool_called",
    "agent_turn_completed",
    "quota_threshold_reached",
    "quota_exceeded",
    "upgrade_prompt_shown",
    "plan_change_requested",
    "plan_change_reviewed",
    "plan_change_cancelled",
    "entitlement_blocked",
    "user_logged_in",
    "user_session_resumed",
    "user_signed_up",
    "user_invited",
    "user_role_changed",
    "user_removed",
    "org_joined",
    "invite_accepted",
    "settings_tab_viewed",
    "settings_save_failed",
    "api_key_created",
    "api_key_revoked",
    "secret_created",
    "activity_tab_viewed",
    "activity_filter_applied",
    "mcp_connect_opened",
    "mcp_url_copied",
    "mcp_client_expanded",
    "mcp_install_link_opened",
    "mcp_client_connected",
    "mcp_tool_called",
    "embed_method_viewed",
    "ui_crashed",
    "chunk_load_failed",
    "api_request_failed",
    "error_shown",
    "permission_denied_shown",
    "empty_state_viewed",
    "search_performed",
    "retry_clicked",
    "bug_report_submitted",
    "support_escalation_submitted",
    "nurture_email_sent",
    "nurture_email_clicked",
    "nurture_resumed",
    "nurture_completed",
    "server_request_failed",
    "onboarding_integration_chosen",
    "onboarding_step_skipped",
    "onboarding_handed_off",
    "page_guide_offered",
    "page_guide_started",
    "page_guide_dismissed",
    "page_guide_step_viewed",
    "page_guide_step_completed",
    "page_guide_completed",
    "page_guide_minimised",
    "page_guide_handed_off",
    "help_menu_opened",
    "help_menu_item_selected",
];
//# sourceMappingURL=analytics.js.map